import { useQuery, useMutation, useQueryClient, UseQueryResult, UseMutationResult } from '@tanstack/react-query';

import { RoleListResponse, Role, QueryOption } from '../@types';
import {
  getRoles,
  getRoleById,
  getActiveRoles,
  createRole,
  createRolePermission,
  getPermission,
  getRolePermissionForRoleEdit,
  updateRole,
  updateRolePermission,
  getRolePermissionForUserEdit,
  PermissionTree
} from '../api/roles';
import { ActiveRolesKey, RolePermissionsKey, RolesKey } from '../configs/roles';

// Permission format from UI
interface RolePermissionItem {
  permissionId: number;
  isGranted: boolean;
}

// Role creation data with permissions
interface RoleCreateData extends Partial<Role> {
  rolePermissions: RolePermissionItem[];
}

// Role update data with permissions
interface RoleUpdateData extends Partial<Role> {
  id: number;
  rolePermissions: RolePermissionItem[];
}

export const useGetAllRoles = (queryOption: QueryOption): UseQueryResult<RoleListResponse> => {
  return useQuery({
    queryKey: [RolesKey, queryOption.page, queryOption.pageSize, queryOption.filterModel],
    queryFn: () => getRoles(queryOption)
  });
};

export const useGetOneRole = (id: number): UseQueryResult<Role> => {
  return useQuery({
    queryKey: [RolesKey, id],
    queryFn: () => getRoleById(id)
  });
};

export const useGetAllActiveRoles = (): UseQueryResult<Role[]> => {
  return useQuery({
    queryKey: [ActiveRolesKey],
    queryFn: () => getActiveRoles()
  });
};

export const useAddRole = (): UseMutationResult<Role, Error, RoleCreateData> => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: RoleCreateData) => {
      const response = await createRole(data);
      const permissionIds = data.rolePermissions.map(p => p.permissionId);
      await createRolePermission(response.id, permissionIds);
      return response;
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [RolesKey]
      });
    }
  });
};

// The permission tree API returns a root node with children, not an array
export const useGetPermissions = (): UseQueryResult<PermissionTree> => {
  return useQuery({
    queryKey: [RolePermissionsKey],
    queryFn: () => getPermission(),
    staleTime: 2 * 60 * 60 * 1000
  });
};

// These queries return a root permission tree node with children
export const useGetRolePermissionsForRoleEdit = (roleId: number): UseQueryResult<PermissionTree | undefined> => {
  return useQuery({
    queryKey: [RolePermissionsKey, roleId],
    queryFn: () => getRolePermissionForRoleEdit(roleId),
    enabled: roleId > 0,
    staleTime: 60 * 60 * 1000
  });
};

export const useGetRolePermissionsForUserEdit = (roleId: number): UseQueryResult<PermissionTree | undefined> => {
  return useQuery({
    queryKey: [RolePermissionsKey, roleId],
    queryFn: () => getRolePermissionForUserEdit(roleId),
    enabled: roleId > 0,
    staleTime: 60 * 60 * 1000
  });
};

export const useUpdateRole = (): UseMutationResult<void, Error, RoleUpdateData> => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: RoleUpdateData) => {
      await updateRole(data.id, data);
      const permissionIds = data.rolePermissions.map(p => p.permissionId);
      await updateRolePermission(data.id, permissionIds);
    },

    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: [RolesKey]
      });
    }
  });
};